import { useHpaV2Status } from 'features/cluster/workload-optimization/_hooks/useHpaV2Status';
import {
  OptimizationType,
  useWorkloadScalingPolicies,
} from 'features/workload-optimization';

import { defaultScalingPolicyFormModel } from '../../_constants';
import { ScalingPolicyFormModel } from '../../_types';
import { apiToFormModel } from '../../_utils';

type UseDefaultScalingPolicyFormModelOptions = {
  includeHpaSettings?: boolean;
};

export const useDefaultScalingPolicyFormModelFormModel = ({
  includeHpaSettings = false,
}: UseDefaultScalingPolicyFormModelOptions = {}): {
  defaultScalingPolicyFormModel?: ScalingPolicyFormModel;
} => {
  const { hpaV2Status } = useHpaV2Status();

  const { policies } = useWorkloadScalingPolicies();

  if (!policies) {
    return {};
  }

  const stabilityPolicy = policies.find(
    (policy) => policy.name === 'stability'
  );

  const balancedPolicy = policies.find((policy) => policy.name === 'balanced');

  if (!stabilityPolicy) {
    // JUST IN CASE, if the API does not return stability policy, or renames it
    return {
      defaultScalingPolicyFormModel: {
        ...defaultScalingPolicyFormModel,
        hpa:
          includeHpaSettings && hpaV2Status === 'available'
            ? defaultScalingPolicyFormModel.hpa
            : undefined,
        name: '',
        optimizationType: OptimizationType.OFF,
      },
    };
  }

  const stabilityFormModel = apiToFormModel({
    scalingPolicy: stabilityPolicy,
    hpaV2Status,
  });

  if (!balancedPolicy) {
    // Balanced policy missing: use stability-derived values but fall back stall detection to static defaults
    return {
      defaultScalingPolicyFormModel: {
        ...stabilityFormModel,
        vpa: {
          ...stabilityFormModel.vpa,
          stallDetection: defaultScalingPolicyFormModel.vpa.stallDetection,
        },
        hpa: includeHpaSettings ? stabilityFormModel.hpa : undefined,
        name: '',
        optimizationType: OptimizationType.OFF,
        assignmentRules: undefined,
      },
    };
  }

  const balancedFormModel = apiToFormModel({
    scalingPolicy: balancedPolicy,
    hpaV2Status,
  });

  return {
    defaultScalingPolicyFormModel: {
      ...stabilityFormModel,
      vpa: {
        ...stabilityFormModel.vpa,
        stallDetection: {
          ...balancedFormModel.vpa.stallDetection,

          cpuStallThresholdPercentage:
            balancedFormModel.vpa.stallDetection.cpuStallThresholdPercentage,
          minPressuredPodPercentage:
            balancedFormModel.vpa.stallDetection.minPressuredPodPercentage,
        },
      },
      hpa: includeHpaSettings ? stabilityFormModel.hpa : undefined,
      name: '',
      optimizationType: OptimizationType.OFF,
      assignmentRules: undefined,
    },
  };
};
